import { useState, useEffect } from 'react';
import axios from 'axios';
import { Database, HardDrive, Activity, RefreshCw, Loader2, AlertTriangle, Cloud, FileStack } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

export default function DbStatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/admin/db-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load DB stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const formatMB = (mb) => (mb == null ? '—' : `${mb.toLocaleString(undefined, { maximumFractionDigits: 2 })} MB`);

  const usedPercent = stats?.used_percent;
  const isCritical = usedPercent != null && usedPercent >= 80;
  const isWarn = usedPercent != null && usedPercent >= 60 && usedPercent < 80;

  return (
    <div className="space-y-6" data-testid="db-stats-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Database Storage</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Live MongoDB usage for <span className="font-mono">{stats?.database || '—'}</span>
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchStats}
          disabled={loading}
          data-testid="db-stats-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {!stats && loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}

      {stats && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              icon={<Database className="w-4 h-4" />}
              label="Total Used"
              value={formatMB(stats.total_used_mb)}
              accent="emerald"
              testid="kpi-total-used"
            />
            <KpiCard
              icon={<HardDrive className="w-4 h-4" />}
              label="Max Capacity"
              value={stats.max_storage_mb ? formatMB(stats.max_storage_mb) : 'Not configured'}
              accent="slate"
              testid="kpi-max"
            />
            <KpiCard
              icon={<Activity className="w-4 h-4" />}
              label="Data Size"
              value={formatMB(stats.data_size_mb)}
              accent="blue"
              testid="kpi-data"
            />
            <KpiCard
              icon={<Activity className="w-4 h-4" />}
              label="Index Size"
              value={formatMB(stats.index_size_mb)}
              accent="amber"
              testid="kpi-index"
            />
          </div>

          {/* Usage progress */}
          {usedPercent != null ? (
            <div
              className={`rounded-xl p-4 border ${
                isCritical
                  ? 'bg-red-50 border-red-200'
                  : isWarn
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-emerald-50 border-emerald-200'
              }`}
              data-testid="db-stats-usage-bar"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-slate-700">
                  {usedPercent}% of {formatMB(stats.max_storage_mb)} used
                </p>
                {isCritical && (
                  <div className="flex items-center gap-1 text-red-600 text-xs font-semibold">
                    <AlertTriangle className="w-4 h-4" />
                    Nearing capacity — consider upgrading
                  </div>
                )}
              </div>
              <div className="h-2 w-full rounded-full bg-white/70 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    isCritical ? 'bg-red-500' : isWarn ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(100, usedPercent)}%` }}
                />
              </div>
            </div>
          ) : (
            <div className="rounded-xl p-4 border border-slate-200 bg-slate-50 text-sm text-slate-600" data-testid="db-stats-no-max">
              Max capacity is not configured. Set the <span className="font-mono bg-white px-1.5 py-0.5 rounded border">DB_MAX_STORAGE_MB</span> environment variable to enable the usage progress bar.
            </div>
          )}

          {/* Per-collection breakdown */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Collections ({stats.collection_count})</h3>
              <span className="text-xs text-slate-500">{stats.object_count.toLocaleString()} total records</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="db-stats-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Collection</th>
                    <th className="text-right px-4 py-2 font-medium">Records</th>
                    <th className="text-right px-4 py-2 font-medium">Data</th>
                    <th className="text-right px-4 py-2 font-medium">Storage</th>
                    <th className="text-right px-4 py-2 font-medium">Indexes</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.collections.map((c) => (
                    <tr key={c.name} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2 font-mono text-xs">{c.name}</td>
                      <td className="px-4 py-2 text-right">{c.count.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-slate-500">{c.size_mb} MB</td>
                      <td className="px-4 py-2 text-right font-semibold">{c.storage_size_mb} MB</td>
                      <td className="px-4 py-2 text-right text-slate-500">{c.index_size_mb} MB</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* R2 File Storage (Cloudflare) */}
          <R2StorageSection r2={stats.r2} r2Error={stats.r2_error} />
        </>
      )}
    </div>
  );
}

function R2StorageSection({ r2, r2Error }) {
  if (r2Error) {
    return (
      <div className="rounded-xl p-4 border border-red-200 bg-red-50 text-sm text-red-700" data-testid="r2-stats-error">
        <div className="flex items-center gap-2 font-semibold mb-1">
          <Cloud className="w-4 h-4" />
          R2 File Storage — unreachable
        </div>
        <p className="text-xs opacity-80 break-all">{r2Error}</p>
      </div>
    );
  }
  if (!r2) return null;

  const usedPercent = r2.used_percent;
  const isCritical = usedPercent != null && usedPercent >= 80;
  const isWarn = usedPercent != null && usedPercent >= 60 && usedPercent < 80;

  const fmtMB = (mb) => (mb == null ? '—' : `${mb.toLocaleString(undefined, { maximumFractionDigits: 2 })} MB`);

  return (
    <div className="space-y-4" data-testid="r2-stats-section">
      <div className="flex items-center gap-2">
        <Cloud className="w-4 h-4 text-slate-600" />
        <h3 className="text-sm font-semibold text-slate-800">
          R2 File Storage <span className="font-mono text-xs text-slate-500 ml-1">{r2.bucket}</span>
        </h3>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={<FileStack className="w-4 h-4" />}
          label="Total Files"
          value={r2.file_count.toLocaleString()}
          accent="blue"
          testid="r2-kpi-count"
        />
        <KpiCard
          icon={<Cloud className="w-4 h-4" />}
          label="Used"
          value={fmtMB(r2.total_size_mb)}
          accent="emerald"
          testid="r2-kpi-used"
        />
        <KpiCard
          icon={<HardDrive className="w-4 h-4" />}
          label="Max Capacity"
          value={r2.max_storage_mb ? fmtMB(r2.max_storage_mb) : 'Not configured'}
          accent="slate"
          testid="r2-kpi-max"
        />
        <KpiCard
          icon={<Activity className="w-4 h-4" />}
          label="Folders"
          value={r2.folders.length.toString()}
          accent="amber"
          testid="r2-kpi-folders"
        />
      </div>

      {usedPercent != null ? (
        <div
          className={`rounded-xl p-4 border ${
            isCritical
              ? 'bg-red-50 border-red-200'
              : isWarn
              ? 'bg-amber-50 border-amber-200'
              : 'bg-emerald-50 border-emerald-200'
          }`}
          data-testid="r2-usage-bar"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-700">
              {usedPercent}% of {fmtMB(r2.max_storage_mb)} used
            </p>
            {isCritical && (
              <div className="flex items-center gap-1 text-red-600 text-xs font-semibold">
                <AlertTriangle className="w-4 h-4" />
                Nearing R2 capacity
              </div>
            )}
          </div>
          <div className="h-2 w-full rounded-full bg-white/70 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                isCritical ? 'bg-red-500' : isWarn ? 'bg-amber-500' : 'bg-emerald-500'
              }`}
              style={{ width: `${Math.min(100, usedPercent)}%` }}
            />
          </div>
        </div>
      ) : (
        <div className="rounded-xl p-4 border border-slate-200 bg-slate-50 text-sm text-slate-600" data-testid="r2-no-max">
          Set <span className="font-mono bg-white px-1.5 py-0.5 rounded border">R2_MAX_STORAGE_MB</span> to surface an R2 usage progress bar.
        </div>
      )}

      {r2.folders.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200">
            <h4 className="text-sm font-semibold text-slate-700">Folders</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="r2-folders-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Folder</th>
                  <th className="text-right px-4 py-2 font-medium">Files</th>
                  <th className="text-right px-4 py-2 font-medium">Size</th>
                </tr>
              </thead>
              <tbody>
                {r2.folders.map((f) => (
                  <tr key={f.name} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs">{f.name}</td>
                    <td className="px-4 py-2 text-right">{f.count.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-semibold">{f.size_mb} MB</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, accent, testid }) {
  const accentMap = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <div className={`rounded-xl p-4 border ${accentMap[accent]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-medium opacity-80">
        {icon}
        {label}
      </div>
      <p className="mt-1 text-xl font-bold">{value}</p>
    </div>
  );
}
